import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from './ScenarioGenerator.module.css';
import { OFFSCREEN_POPOVER_POSITION, useAnchoredPopoverPosition } from '../../../hooks/useAnchoredPopoverPosition';
import { listTtsVoices } from '../../../features/scenarios/api';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ProviderSegmentedControl } from './ProviderSegmentedControl';
import {
  NARRATION_PROVIDER_OPTIONS,
  SERVER_VOICE_FILTER_OPTIONS,
  STANDARD_VOICE_ACCENT_LABEL,
  VOICE_FILTER_OPTION_ORDER,
  sortByPreferredOrder,
  defaultLicensedVoiceFilter,
  providerFiltersVoicesOnServer,
  providerSupportsLicensedFilter,
  type NarrationProviderCode,
} from './constants';
import { findVoiceInCache, ttsVoicesQueryKey, TTS_VOICES_STALE_TIME, type VoiceListFilters } from './narrationVoiceProvider';
import { useVoiceFiltersSupported } from './hooks/useNarrationCapabilities';

/** 「権利処理済み」をオフにする際の注意喚起 (段落ごと)。JSX の改行で文中に空白が入らないよう文字列で持つ */
const LICENSED_OFF_WARNING = [
  'オフにすると、Fish Audio の公開ライブラリに一般ユーザーが投稿したボイスも一覧に含まれます。'
    + '投稿ボイスには、実在の人物や番組・キャラクターの声を本人の許諾なく再現したものが含まれている可能性があり、'
    + 'Fish Audio 側での権利処理は行われていません。',
  'こうしたボイスを広告や商用コンテンツに使用した場合、パブリシティ権・肖像権などの侵害を指摘されるおそれがあります。'
    + '利用は自己責任となりますので、権利関係に問題がないことを確認のうえご使用ください。',
] as const;

export type VoiceSelectVoice = {
  voice_id: string;
  name: string;
  labels?: Record<string, string>;
  category?: string;
  preview_url?: string;
  high_quality_base_model_ids?: string[];
  /** 補足 (Fish Audio: 提供者名 / ElevenLabs: 名前の " - " 以降の説明文 / Gemini: 声の説明)。BE が分割済み */
  subtitle?: string | null;
  /** Fish Audio: Fish Official が権利処理して提供しているボイス */
  licensed?: boolean;
};

/**
 * ボイスセレクト（フィルター付き、日本語ボイスのみ）。
 *
 * `voices` は親が取得した無条件一覧。API 側で絞るプロバイダ (providerFiltersVoicesOnServer =
 * Fish Audio) では、性別 / 年代 / 権利処理済みの条件ごとにここで一覧を取り直し、選択肢はその
 * 結果を使う (無条件一覧はスコア上位 100 件しか無く、取得後に絞ると偏るため)。
 * それ以外 (ElevenLabs) は従来どおり `voices` をフロントで絞る。
 */
export function VoiceSelect({
  voices,
  value,
  onChange,
  defaultGenderFilter,
  defaultAgeFilter,
  defaultAccentCode,
  preferStandardAccent,
  provider,
  onProviderChange,
  loading,
}: {
  voices: VoiceSelectVoice[];
  /** 親が一覧を取得中 (選択肢が空でも「該当なし」ではなく「読み込み中...」と出す) */
  loading?: boolean;
  value: string;
  onChange: (voiceId: string) => void;
  /** 推奨ナレーターの性別 / 年代。渡されると既定フィルターにして先頭の声を自動選択する */
  defaultGenderFilter?: string;
  defaultAgeFilter?: string;
  /** 方言付きシナリオの地域 (Gemini の accent の API 生値)。渡されると「地域」の既定の絞り込みにする */
  defaultAccentCode?: string;
  /**
   * 方言の指定が無いシナリオ。Gemini では「地域: 標準語」を既定の絞り込みにする (方言の声を混ぜない)。
   * 一覧の表示を絞るだけで、先頭の声の自動選択はしない (選択済みの声を書き換えない)
   */
  preferStandardAccent?: boolean;
  provider?: NarrationProviderCode;
  onProviderChange?: (provider: NarrationProviderCode) => void;
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [genderFilter, setGenderFilter] = useState('');
  const [accentFilter, setAccentFilter] = useState('');
  const [ageFilter, setAgeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hqFilter, setHqFilter] = useState(false);
  // 「権利処理済み」(Fish Audio のみ)。既定はブランド設定で決まる (SAITE はオン / 自社ブランドはオフ)。
  // 並び順には影響しない (API のスコア順のまま)。オン → オフはリスクの注意喚起を挟む
  const [licensedFilter, setLicensedFilter] = useState(() => (provider ? defaultLicensedVoiceFilter(provider) : false));
  const [showLicensedOffConfirm, setShowLicensedOffConfirm] = useState(false);

  const queryClient = useQueryClient();
  const serverFiltered = !!provider && providerFiltersVoicesOnServer(provider);
  // 性別・年代での絞り込みが効くか (Gemini は BE の今の既定モデルで変わる)。provider 未指定は従来どおり表示する
  const voiceFiltersSupported = useVoiceFiltersSupported(provider);
  const serverFilters = useMemo<VoiceListFilters>(
    () => ({ gender: genderFilter || undefined, age: ageFilter || undefined, licensed: licensedFilter || undefined }),
    [genderFilter, ageFilter, licensedFilter],
  );
  // 絞り込み条件ごとの一覧 (API 側で絞るプロバイダのみ)。条件なしは親の無条件一覧と同じ queryKey を共有する
  const { data: serverVoices, isLoading: serverLoading } = useQuery({
    queryKey: provider ? ttsVoicesQueryKey(provider, serverFilters) : (['tts-voices', 'none'] as const),
    queryFn: () => listTtsVoices<VoiceSelectVoice>(provider as NarrationProviderCode, serverFilters),
    enabled: serverFiltered,
    staleTime: TTS_VOICES_STALE_TIME,
  });

  const [, setPreviewPlaying] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterModalRef = useRef<HTMLDivElement>(null);
  // ドロップダウン / フィルターとも、既定はトリガーの右下 (左端を合わせて下へ) に出し、
  // 実寸を測って画面からはみ出す分だけ寄せる (左カラムなど画面端に近い配置でも切れない)。
  // 以前フィルターは右端合わせ (左へ広がる) だったため、画面左寄りのトリガーでは左にはみ出していた。
  const dropdownPos = useAnchoredPopoverPosition(showDropdown, triggerRef, dropdownRef) ?? OFFSCREEN_POPOVER_POSITION;
  const filterPos = useAnchoredPopoverPosition(showFilters, filterBtnRef, filterModalRef) ?? OFFSCREEN_POPOVER_POSITION;
  // 選択肢の幅はトリガー幅を下限にする (開いた瞬間のトリガー幅を採用)
  const [dropdownMinWidth, setDropdownMinWidth] = useState(0);

  const jaVoices = voices.filter(v => v.labels?.language === 'ja');

  // フィルタオプションを動的に抽出。API 側で絞るプロバイダは性別 / 年代を固定にする
  // (無条件一覧に該当が無い値のチップが消えて選べなくなるのを防ぐ)。
  // 並びは全プロバイダ共通 (男性・女性・中性的 / 若者・中高年 / 標準語・関西弁・博多弁)
  const filterOptions = useMemo(() => {
    const genders = sortByPreferredOrder(serverFiltered
      ? [...SERVER_VOICE_FILTER_OPTIONS.genders]
      : ([...new Set(jaVoices.map(v => v.labels?.gender).filter(Boolean))] as string[]), VOICE_FILTER_OPTION_ORDER.genders);
    const accents = sortByPreferredOrder(
      [...new Set(jaVoices.map(v => v.labels?.accent).filter(Boolean))] as string[], VOICE_FILTER_OPTION_ORDER.accents,
    );
    const ages = sortByPreferredOrder(serverFiltered
      ? [...SERVER_VOICE_FILTER_OPTIONS.ages]
      : ([...new Set(jaVoices.map(v => v.labels?.age).filter(Boolean))] as string[]), VOICE_FILTER_OPTION_ORDER.ages);
    const categories = [...new Set(jaVoices.map(v => v.category).filter(Boolean))] as string[];
    return { genders, accents, ages, categories };
  }, [jaVoices, serverFiltered]);

  // クライアントサイドフィルタ（ソート: professional > HQ > premade > その他）。
  // API 側で絞るプロバイダは性別 / 年代 / 権利処理済みを API に任せ (取得済み一覧はその条件で
  // 絞られている)、ここではカテゴリ等だけ掛ける
  const filteredVoices = useMemo(() => {
    const source = serverFiltered ? (serverVoices ?? []).filter(v => v.labels?.language === 'ja') : jaVoices;
    const filtered = source.filter(v => {
      if (!serverFiltered && genderFilter && v.labels?.gender !== genderFilter) return false;
      if (accentFilter && v.labels?.accent !== accentFilter) return false;
      if (!serverFiltered && ageFilter && v.labels?.age !== ageFilter) return false;
      if (categoryFilter && v.category !== categoryFilter) return false;
      if (hqFilter && !(v.high_quality_base_model_ids?.length)) return false;
      return true;
    });
    // API 側で絞るプロバイダは API の並び (ElevenLabs: 直近 1 年の利用実績順 / Fish: スコア順) を正本にし、
    // フロントで Pro / HQ 等のカテゴリー順に並べ替えない (Pro ブロック → HQ ブロックに分断されるため)
    if (serverFiltered) return filtered;
    return filtered.sort((a, b) => {
      const rank = (v: typeof a) => {
        if (v.category === 'professional') return 0;
        if (v.high_quality_base_model_ids?.length) return 1;
        if (v.category === 'premade') return 2;
        return 3;
      };
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      // 同ランクの場合、IROHA > TAKUMI > KONOHA > その他の順に優先
      const nameRank = (v: typeof a) => {
        const n = v.name?.toUpperCase() ?? '';
        if (n.includes('IROHA')) return 0;
        if (n.includes('TAKUMI')) return 1;
        if (n.includes('KONOHA')) return 2;
        return 3;
      };
      return nameRank(a) - nameRank(b);
    });
  }, [jaVoices, serverFiltered, serverVoices, genderFilter, accentFilter, ageFilter, categoryFilter, hqFilter]);

  // Gemini推奨フィルタ適用時に先頭ボイスを自動選択
  // useRefではなくuseStateを使い、フィルタ更新とバッチされるようにする
  const [pendingAutoSelect, setPendingAutoSelect] = useState(false);

  // プロバイダ切替時はフィルタ state を全リセットする。
  // Gemini はプリセット音声で gender/age/category/HQ ラベルを持たないため、
  // ElevenLabs 用に残ったフィルタ値があると全件除外され、しかも該当チップが
  // 描画されず解除できない (詰む) のを防ぐ。
  // この effect は下の推奨 (defaultGenderFilter / defaultAgeFilter) effect より前に宣言し、
  // 両者が同時に発火した場合 (プロバイダ切替) は推奨側を優先させる。
  useEffect(() => {
    setGenderFilter('');
    setAccentFilter('');
    setAgeFilter('');
    setCategoryFilter('');
    setHqFilter(false);
    setLicensedFilter(provider ? defaultLicensedVoiceFilter(provider) : false);
    setShowLicensedOffConfirm(false);
    setShowFilters(false);
  }, [provider]);

  // 権利処理済みをオフにする操作。既定オンのブランドでは注意喚起 (ConfirmDialog) を挟み、
  // 承認したときだけオフにする。既定オフのブランドは自分でオンにした設定なのでそのまま戻せる
  const handleLicensedChange = (checked: boolean) => {
    if (checked) {
      setLicensedFilter(true);
      return;
    }
    if (provider && defaultLicensedVoiceFilter(provider)) {
      // フィルターのポップオーバー (z-index 10000) は確認ダイアログ (--z-modal-nested) より前面に
      // 出るため、ダイアログを出す前に閉じる
      setShowFilters(false);
      setShowLicensedOffConfirm(true);
      return;
    }
    setLicensedFilter(false);
  };

  // 推奨ナレーター (性別 / 年代) を既定フィルターとして適用し、先頭の声を自動選択する。
  // provider も依存に含め、上のリセット effect の後に再適用する (ElevenLabs → Fish のように
  // 既定値そのものが変わらない切替では以前は発火せず、推奨がリセットで消えていた)。
  useEffect(() => {
    if (defaultGenderFilter === undefined && defaultAgeFilter === undefined) return;
    setGenderFilter(defaultGenderFilter ?? '');
    setAgeFilter(defaultAgeFilter ?? '');
    setPendingAutoSelect(true);
  }, [defaultGenderFilter, defaultAgeFilter, provider]);

  // 方言付きシナリオの地域を既定の絞り込みにする。絞り込みは表示名 (labels.accent) で掛けるので、
  // 一覧が揃ってから API 生値 (labels.accent_code) を表示名に写す。推奨ナレーターと同じく
  // 依存値 (地域・プロバイダ) が変わったときだけ適用し、ユーザーが外した絞り込みは戻さない。
  // 「適用済み」を ref で持つと、StrictMode の effect 再実行で上のリセットだけが後から効いて消える
  const dialectAccentLabel = provider === 'gemini' && defaultAccentCode
    ? jaVoices.find(v => v.labels?.accent_code === defaultAccentCode)?.labels?.accent
    : undefined;
  // 方言の指定が無いシナリオは「標準語」(プリセットも含む) で絞る。こちらは表示の絞り込みだけで、
  // 先頭の声の自動選択はしない。一覧が揃ってから (標準語の声があるときだけ) 適用する
  const standardAccentLabel = provider === 'gemini' && !defaultAccentCode && preferStandardAccent
    && jaVoices.some(v => v.labels?.accent === STANDARD_VOICE_ACCENT_LABEL)
    ? STANDARD_VOICE_ACCENT_LABEL
    : undefined;
  const defaultAccentLabel = dialectAccentLabel ?? standardAccentLabel;
  // 一覧が推奨 (性別・年代) より後に届くと、自動選択が地域の絞り込みより先に走るので、
  // 地域を適用するまでは自動選択を待たせる
  const [appliedAccentLabel, setAppliedAccentLabel] = useState('');
  useEffect(() => {
    if (!defaultAccentLabel) return;
    setAccentFilter(defaultAccentLabel);
    setAppliedAccentLabel(defaultAccentLabel);
    if (dialectAccentLabel) setPendingAutoSelect(true);
  }, [defaultAccentLabel, dialectAccentLabel, provider]);

  useEffect(() => {
    if (defaultAccentLabel && appliedAccentLabel !== defaultAccentLabel) return;
    if (pendingAutoSelect && filteredVoices.length > 0) {
      onChange(filteredVoices[0].voice_id);
      setPendingAutoSelect(false);
    }
  }, [pendingAutoSelect, filteredVoices, onChange, defaultAccentLabel, appliedAccentLabel]);

  const activeFilterCount = [genderFilter, accentFilter, ageFilter, categoryFilter].filter(Boolean).length + (hqFilter ? 1 : 0) + (licensedFilter ? 1 : 0);
  // 高品質 (HQ) チップは ElevenLabs 固有の概念。該当ボイスが 1 つも無いプロバイダでは出さない
  const hasHqVoices = useMemo(() => jaVoices.some(v => v.high_quality_base_model_ids?.length), [jaVoices]);
  const showLicensedFilter = !!provider && providerSupportsLicensedFilter(provider);

  // 選択中の声。絞り込み経由で選んだ声は現在の選択肢にも無条件一覧にも無いことがあるため、
  // 同プロバイダの全キャッシュ (絞り込み済み一覧・1 件取得) からも探す
  const selectedVoice = value
    ? filteredVoices.find(v => v.voice_id === value)
      || voices.find(v => v.voice_id === value)
      || (provider ? findVoiceInCache<VoiceSelectVoice>(queryClient, provider, value) : undefined)
      || null
    : null;

  const stopPreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewPlaying(false);
    setPreviewingVoiceId(null);
  };

  const handlePreview = (voiceId: string, url?: string) => {
    if (previewingVoiceId === voiceId) {
      stopPreview();
      return;
    }
    stopPreview();
    if (!url) return;
    const audio = new Audio(url);
    previewAudioRef.current = audio;
    setPreviewPlaying(true);
    setPreviewingVoiceId(voiceId);
    audio.play();
    audio.onended = () => { stopPreview(); };
    audio.onerror = () => { stopPreview(); };
  };

  // ドロップダウン項目に添える属性表示。
  // - label: ElevenLabs のカテゴリー (Pro = プロ声優 / HQ = 高品質クローン)。枠線なしのグレー塗りの小さなラベル
  // - badge: 枠線付きのバッジ (高品質モデル対応 = high_quality_base_model_ids / Fish Audio の権利処理済み)
  const voiceTags = (v: VoiceSelectVoice): Array<{ text: string; kind: 'label' | 'badge' }> => {
    const tags: Array<{ text: string; kind: 'label' | 'badge' }> = [];
    if (v.category === 'professional') tags.push({ text: 'Pro', kind: 'label' });
    if (v.category === 'high_quality') tags.push({ text: 'HQ', kind: 'label' });
    if (v.high_quality_base_model_ids?.length) tags.push({ text: '高品質モデル', kind: 'badge' });
    if (v.licensed) tags.push({ text: '権利処理済み', kind: 'badge' });
    return tags;
  };

  const labelMap: Record<string, string> = {
    // gender
    male: '男性', female: '女性', neutral: '中性的',
    // age
    young: '若者', middle_aged: '中高年', old: '年配',
    // category
    // high_quality は ElevenLabs 共有ライブラリのカテゴリ (高品質クローン)。「品質」の
    // 「高品質モデル対応」(high_quality_base_model_ids) とは別概念で、実データでも重ならない
    premade: 'プリメイド', cloned: 'クローン', generated: '生成', professional: 'プロ声優', high_quality: '高品質クローン',
    // Gemini: プリセット 30 種 (ライブラリの声は BE が用途を日本語のカテゴリーで返す)
    preset: 'プリセット',
    // Fish Audio は公開ライブラリのみで category を持たない (カテゴリー行は出ない)
  };
  const toJa = (v: string) => labelMap[v] || v;

  const renderChips = (
    label: string,
    options: string[],
    current: string,
    setter: (v: string) => void,
  ) => {
    if (options.length === 0) return null;
    return (
      <div className={styles.voiceFilterRow}>
        <span className={styles.voiceFilterLabel}>{label}</span>
        <div className={styles.voiceFilterChips}>
          <button
            type="button"
            className={`${styles.voiceFilterChip} ${!current ? styles.voiceFilterChipActive : ''}`}
            onClick={() => setter('')}
          >すべて</button>
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              className={`${styles.voiceFilterChip} ${current === opt ? styles.voiceFilterChipActive : ''}`}
              onClick={() => setter(current === opt ? '' : opt)}
            >{toJa(opt)}</button>
          ))}
        </div>
      </div>
    );
  };

  const openDropdown = () => {
    if (triggerRef.current) {
      setDropdownMinWidth(triggerRef.current.getBoundingClientRect().width);
    }
    setShowDropdown(true);
  };

  return (
    <div className={styles.voiceSelectWrapper}>
      {provider && onProviderChange && (
        <ProviderSegmentedControl
          options={NARRATION_PROVIDER_OPTIONS}
          value={provider}
          onChange={onProviderChange}
        />
      )}
      <div className={styles.voiceSelectGroup}>
        <div
          ref={triggerRef}
          className={styles.voiceSelectTrigger}
          onClick={() => showDropdown ? setShowDropdown(false) : openDropdown()}
        >
          <span className={styles.voiceSelectLabel}>
            {/* API 側で絞るプロバイダは件数が条件ごとの上位 100 件で全体を表さないため出さない */}
            {selectedVoice ? selectedVoice.name : (loading && voices.length === 0) ? '読み込み中...' : serverFiltered ? '声を選択' : `声を選択 (${filteredVoices.length})`}
          </span>
          <span className={styles.voiceSelectArrow}><svg width="8" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l3 4 3-4" /></svg></span>
        </div>
        {showDropdown && createPortal(
          <>
            <div className={styles.voiceDropdownOverlay} onClick={() => setShowDropdown(false)} />
            <div
              ref={dropdownRef}
              className={`modal-surface ${styles.voiceDropdown}`}
              style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownMinWidth, width: 'auto' }}
            >
              {filteredVoices.length === 0 ? (
                <div className={styles.voiceDropdownEmpty}>
                  {(serverFiltered && serverLoading) || (loading && voices.length === 0) ? '読み込み中...' : '該当するボイスがありません'}
                </div>
              ) : (
                filteredVoices.map(v => (
                  <div
                    key={v.voice_id}
                    className={`${styles.voiceDropdownItem} ${v.voice_id === value ? styles.voiceDropdownItemActive : ''}`}
                    onClick={() => {
                      stopPreview();
                      onChange(v.voice_id);
                      setShowDropdown(false);
                    }}
                  >
                    <span className={styles.voiceDropdownName}>
                      {v.name}
                      {v.subtitle && (
                        // 長い説明文 (ElevenLabs) は CSS で切り詰め、全文はツールチップで見せる
                        <span className={styles.voiceDropdownSubtitle} title={v.subtitle}>{v.subtitle}</span>
                      )}
                    </span>
                    {voiceTags(v).map(tag => (
                      <span
                        key={tag.text}
                        className={tag.kind === 'label' ? styles.voiceDropdownLabel : styles.voiceDropdownTag}
                        title={tag.text === 'Pro' ? 'プロ声優 (Professional Voice Clone)' : tag.text === 'HQ' ? '高品質クローン (High Quality)' : undefined}
                      >{tag.text}</span>
                    ))}
                    {v.preview_url && (
                      <button
                        type="button"
                        className={`${styles.voicePreviewBtn} ${previewingVoiceId === v.voice_id ? styles.voicePreviewBtnActive : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(v.voice_id, v.preview_url);
                        }}
                        title="プレビュー"
                      >
                        {previewingVoiceId === v.voice_id ? (
                          <svg width="16" height="16" viewBox="0 0 16 16">
                            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                            <rect x="5.5" y="5.5" width="5" height="5" rx="0.5" fill="currentColor" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 16 16">
                            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                            <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </>,
          document.body,
        )}
      </div>
      {/* 性別・年代のラベルが無い一覧 (Gemini のプリセットだけ = 3.1 旧経路) ではフィルタが機能しないため非表示 */}
      {(!provider || voiceFiltersSupported) && (
      <>
      <button
        ref={filterBtnRef}
        type="button"
        className={`${styles.voiceFilterToggle} ${activeFilterCount > 0 ? styles.voiceFilterToggleActive : ''}`}
        onClick={() => setShowFilters(!showFilters)}
        title="フィルター"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
      </button>
      {showFilters && createPortal(
        <>
          <div className={styles.voiceFilterOverlay} onClick={() => setShowFilters(false)} />
          <div
            ref={filterModalRef}
            className={`modal-surface ${styles.voiceFilterModal}`}
            style={{ position: 'fixed', top: filterPos.top, left: filterPos.left }}
          >
            <div className={styles.voiceFilterModalHeader}>
              <span>フィルター</span>
              <button type="button" className={styles.voiceFilterModalClose} onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className={styles.voiceFilterModalBody}>
              {hasHqVoices && (
              <div className={styles.voiceFilterRow}>
                <span className={styles.voiceFilterLabel}>品質</span>
                <div className={styles.voiceFilterChips}>
                  <button
                    type="button"
                    className={`${styles.voiceFilterChip} ${!hqFilter ? styles.voiceFilterChipActive : ''}`}
                    onClick={() => setHqFilter(false)}
                  >すべて</button>
                  <button
                    type="button"
                    className={`${styles.voiceFilterChip} ${hqFilter ? styles.voiceFilterChipActive : ''}`}
                    onClick={() => setHqFilter(!hqFilter)}
                  >高品質モデル対応</button>
                </div>
              </div>
              )}
              {renderChips('性別', filterOptions.genders, genderFilter, setGenderFilter)}
              {renderChips('年齢', filterOptions.ages, ageFilter, setAgeFilter)}
              {/* 地域 (話し言葉の地域: 標準語 / 関西弁 / 博多弁 等)。Gemini の拡張ボイスライブラリのみ。
                  ElevenLabs も labels.accent を持つが、値の揺れが大きいため出さない */}
              {provider === 'gemini' && renderChips('地域', filterOptions.accents, accentFilter, setAccentFilter)}
              {renderChips('カテゴリー', filterOptions.categories, categoryFilter, setCategoryFilter)}
              {showLicensedFilter && (
              <div className={styles.voiceFilterRow}>
                <span className={styles.voiceFilterLabel}>権利</span>
                <label
                  className={styles.voiceFilterCheck}
                  title="Fish Audio 公式が権利処理して提供しているボイスのみ表示します (コミュニティ投稿の声を除く)"
                >
                  <input
                    type="checkbox"
                    checked={licensedFilter}
                    onChange={(e) => handleLicensedChange(e.target.checked)}
                  />
                  権利処理済み
                </label>
              </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
      </>
      )}
      <ConfirmDialog
        isOpen={showLicensedOffConfirm}
        variant="warning"
        title="権利処理済み以外のボイスも表示しますか？"
        messageAlign="left"
        message={(
          <>
            {LICENSED_OFF_WARNING[0]}
            <br />
            <br />
            {LICENSED_OFF_WARNING[1]}
          </>
        )}
        confirmLabel="理解して表示する"
        cancelLabel="キャンセル"
        onConfirm={() => {
          setLicensedFilter(false);
          setShowLicensedOffConfirm(false);
        }}
        onCancel={() => setShowLicensedOffConfirm(false)}
      />
    </div>
  );
}
